<template>
  <div class="input-preview">
    <!-- Text Preview -->
    <div v-if="inputType === 'text'" class="input-preview__text">
      <div class="input-preview__header">
        <div class="input-preview__icon">
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <polyline points="4,7 4,4 20,4 20,7" />
            <line x1="9" y1="20" x2="15" y2="20" />
            <line x1="12" y1="4" x2="12" y2="20" />
          </svg>
        </div>
        <span class="input-preview__title">{{ t("text_content") }}</span>
        <div class="input-preview__actions">
          <button class="input-preview__btn" @click="$emit('edit')">
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" />
              <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
            </svg>
          </button>
          <button
            class="input-preview__btn input-preview__btn--danger"
            @click="$emit('clear')"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
      </div>
      <div class="input-preview__content">
        <p v-if="textContent" class="input-preview__text-content">
          {{ truncatedText }}
        </p>
        <button v-else class="input-preview__add-btn" @click="$emit('edit')">
          + {{ t("add_text_content") }}
        </button>
      </div>
    </div>

    <!-- Image Preview -->
    <div v-else-if="inputType === 'image'" class="input-preview__file">
      <div class="input-preview__header">
        <div class="input-preview__icon">
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
            <circle cx="8.5" cy="8.5" r="1.5" />
            <polyline points="21,15 16,10 5,21" />
          </svg>
        </div>
        <span class="input-preview__title">{{ t("image") }}</span>
        <div class="input-preview__actions">
          <button class="input-preview__btn" @click="$emit('reselect')">
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <polyline points="23,4 23,10 17,10" />
              <path d="M20.49 15a9 9 0 11-2.12-9.36L23 10" />
            </svg>
          </button>
          <button
            class="input-preview__btn input-preview__btn--danger"
            @click="$emit('clear')"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
      </div>
      <div class="input-preview__content">
        <div v-if="selectedFile" class="input-preview__image-container">
          <img
            :src="filePreviewUrl"
            :alt="selectedFile.name"
            class="input-preview__image"
          />
          <span class="input-preview__filename">{{ selectedFile.name }}</span>
        </div>
        <button
          v-else
          class="input-preview__add-btn"
          @click="$emit('reselect')"
        >
          + {{ t("select_image") }}
        </button>
      </div>
    </div>

    <!-- Audio Preview -->
    <div v-else-if="inputType === 'audio'" class="input-preview__file">
      <div class="input-preview__header">
        <div class="input-preview__icon">
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <path d="M12 1a3 3 0 00-3 3v8a3 3 0 006 0V4a3 3 0 00-3-3z" />
            <path d="M19 10v2a7 7 0 01-14 0v-2" />
            <line x1="12" y1="19" x2="12" y2="23" />
            <line x1="8" y1="23" x2="16" y2="23" />
          </svg>
        </div>
        <span class="input-preview__title">{{ t("audio") }}</span>
        <div class="input-preview__actions">
          <button class="input-preview__btn" @click="$emit('reselect')">
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <polyline points="23,4 23,10 17,10" />
              <path d="M20.49 15a9 9 0 11-2.12-9.36L23 10" />
            </svg>
          </button>
          <button
            class="input-preview__btn input-preview__btn--danger"
            @click="$emit('clear')"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
      </div>
      <div class="input-preview__content">
        <div v-if="selectedFile" class="input-preview__audio-container">
          <div class="input-preview__audio-icon">
            <svg
              width="48"
              height="48"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="1.5"
            >
              <path d="M9 18V5l12-2v13" />
              <circle cx="6" cy="18" r="3" />
              <circle cx="18" cy="16" r="3" />
            </svg>
          </div>
          <span class="input-preview__filename">{{ selectedFile.name }}</span>
          <span class="input-preview__filesize">{{
            formatFileSize(selectedFile.size)
          }}</span>
        </div>
        <button
          v-else
          class="input-preview__add-btn"
          @click="$emit('reselect')"
        >
          + {{ t("select_audio") }}
        </button>
      </div>
    </div>

    <!-- File Preview (PDF) -->
    <div v-else-if="inputType === 'file'" class="input-preview__file">
      <div class="input-preview__header">
        <div class="input-preview__icon">
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
            <polyline points="14,2 14,8 20,8" />
          </svg>
        </div>
        <span class="input-preview__title">{{ t("file") }}</span>
        <div class="input-preview__actions">
          <button class="input-preview__btn" @click="$emit('reselect')">
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <polyline points="23,4 23,10 17,10" />
              <path d="M20.49 15a9 9 0 11-2.12-9.36L23 10" />
            </svg>
          </button>
          <button
            class="input-preview__btn input-preview__btn--danger"
            @click="$emit('clear')"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
      </div>
      <div class="input-preview__content">
        <div v-if="selectedFile" class="input-preview__pdf-container">
          <div class="input-preview__pdf-icon">
            <svg
              width="48"
              height="48"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#ef4444"
              stroke-width="1.5"
            >
              <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
              <polyline points="14,2 14,8 20,8" />
              <line x1="16" y1="13" x2="8" y2="13" />
              <line x1="16" y1="17" x2="8" y2="17" />
            </svg>
          </div>
          <span class="input-preview__filename">{{ selectedFile.name }}</span>
          <span class="input-preview__filesize">{{
            formatFileSize(selectedFile.size)
          }}</span>
        </div>
        <button
          v-else
          class="input-preview__add-btn"
          @click="$emit('reselect')"
        >
          + {{ t("select_file") }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { computed, ref, watch } from "vue";
import { MiaInputType } from "../../types";
import { useI18n } from "../../i18n";

export default {
  name: "InputPreview",
  props: {
    inputType: {
      type: String,
      required: true,
    },
    textContent: {
      type: String,
      default: null,
    },
    selectedFile: {
      type: [File, Object],
      default: null,
    },
  },
  emits: ["edit", "clear", "reselect"],
  setup(props) {
    const { t } = useI18n();
    const filePreviewUrl = ref(null);

    const truncatedText = computed(() => {
      if (!props.textContent) return "";
      if (props.textContent.length > 200) {
        return props.textContent.substring(0, 200) + "...";
      }
      return props.textContent;
    });

    const formatFileSize = (bytes) => {
      if (!bytes) return "0 B";
      const k = 1024;
      const sizes = ["B", "KB", "MB", "GB"];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + " " + sizes[i];
    };

    // Create preview URL for images
    watch(
      () => props.selectedFile,
      (file) => {
        if (file && props.inputType === MiaInputType.IMAGE) {
          if (filePreviewUrl.value) {
            URL.revokeObjectURL(filePreviewUrl.value);
          }
          filePreviewUrl.value = URL.createObjectURL(file);
        } else {
          if (filePreviewUrl.value) {
            URL.revokeObjectURL(filePreviewUrl.value);
          }
          filePreviewUrl.value = null;
        }
      },
      { immediate: true }
    );

    return {
      t,
      truncatedText,
      formatFileSize,
      filePreviewUrl,
    };
  },
};
</script>

<style scoped>
.input-preview {
  padding: 0 12px;
}

.input-preview__text,
.input-preview__file {
  background: white;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  overflow: hidden;
}

.input-preview__header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
}

.input-preview__icon {
  color: var(--mia-primary);
  display: flex;
  align-items: center;
}

.input-preview__title {
  flex: 1;
  font-size: 14px;
  font-weight: 600;
  color: #1f2937;
}

.input-preview__actions {
  display: flex;
  gap: 8px;
}

.input-preview__btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  cursor: pointer;
  color: #6b7280;
  transition: all 0.2s ease;
}

.input-preview__btn:hover {
  background: #f3f4f6;
  color: var(--mia-primary);
  border-color: var(--mia-primary);
}

.input-preview__btn--danger:hover {
  background: #fef2f2;
  color: #ef4444;
  border-color: #ef4444;
}

.input-preview__content {
  padding: 16px;
}

.input-preview__text-content {
  font-size: 14px;
  color: #374151;
  line-height: 1.6;
  margin: 0;
  white-space: pre-wrap;
  word-break: break-word;
}

.input-preview__add-btn {
  width: 100%;
  padding: 24px;
  background: #f9fafb;
  border: 2px dashed #d1d5db;
  border-radius: 8px;
  font-size: 14px;
  color: #6b7280;
  cursor: pointer;
  transition: all 0.2s ease;
}

.input-preview__add-btn:hover {
  background: #f3f4f6;
  border-color: var(--mia-primary);
  color: var(--mia-primary);
}

.input-preview__image-container,
.input-preview__audio-container,
.input-preview__pdf-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

.input-preview__image {
  max-width: 100%;
  max-height: 200px;
  border-radius: 8px;
  object-fit: contain;
}

.input-preview__audio-icon,
.input-preview__pdf-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 80px;
  height: 80px;
  background: rgba(0, 196, 68, 0.12);
  border-radius: 12px;
  color: var(--mia-primary);
}

.input-preview__filename {
  font-size: 14px;
  font-weight: 500;
  color: #1f2937;
  text-align: center;
  word-break: break-all;
}

.input-preview__filesize {
  font-size: 12px;
  color: #6b7280;
}
</style>
